"use client";

import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { markArticleRead, unmarkArticleRead } from "@/app/magazine/actions";
import { useReader } from "@/components/magazine/reader-context";
import { createClient } from "@/lib/supabase/client";
type LoadStatus = "loading" | "ready" | "error";
interface ReadState {
  retry: () => void;
  setRead: (slug: string, read: boolean) => Promise<void>;
  slugs: ReadonlySet<string>;
  status: LoadStatus;
}
const ReadStateContext = createContext<ReadState>({
  retry: () => undefined,
  setRead: () => Promise.reject(new Error("Read state is unavailable")),
  slugs: new Set(),
  status: "loading"
});
interface ProviderState {
  slugs: ReadonlySet<string>;
  status: LoadStatus;
  userId: string | null | undefined;
}
function initialState(userId: ProviderState["userId"]): ProviderState {
  return {
    slugs: new Set<string>(),
    status: userId === null ? "ready" : "loading",
    userId
  };
}
export function ReadStateProvider({
  children
}: {
  children: ReactNode;
}) {
  const userId = useReader()?.id;
  const [state, setState] = useState(() => initialState(userId));
  const [attempt, setAttempt] = useState(0);
  const {
    slugs,
    status
  } = state;
  if (state.userId !== userId) {
    setState(initialState(userId));
  }
  useEffect(() => {
    if (!userId) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const {
          data,
          error
        } = await createClient().from("article_reads").select("article_slug").eq("user_id", userId);
        if (error) {
          throw error;
        }
        if (!cancelled) {
          setState(previous_0 => ({
            ...previous_0,
            slugs: new Set((data ?? []).map(row => row.article_slug)),
            status: "ready"
          }));
        }
      } catch {
        if (!cancelled) {
          setState(previous => ({
            ...previous,
            status: "error"
          }));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, attempt]);
  const retry = () => {
    setState(previous_1 => ({
      ...previous_1,
      status: "loading"
    }));
    setAttempt(value => value + 1);
  };
  const setRead = async (slug: string, read: boolean) => {
    if (!userId || status !== "ready") {
      throw new Error("Read state is unavailable");
    }
    await (read ? markArticleRead(slug) : unmarkArticleRead(slug));
    setState(previous_2 => {
      if (previous_2.userId !== userId) {
        return previous_2;
      }
      const next = new Set(previous_2.slugs);
      if (read) {
        next.add(slug);
      } else {
        next.delete(slug);
      }
      return {
        ...previous_2,
        slugs: next
      };
    });
  };
  return <ReadStateContext.Provider value={{
    retry,
    setRead,
    slugs,
    status
  }}>
      {children}
    </ReadStateContext.Provider>;
}
export function useReadState() {
  return useContext(ReadStateContext);
}